import React, { useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import TargetTitle from '../../activation-code/components/TargetTitle';
import { useByTarget, useSummaryOptions } from '../hooks/useActivationReport';
import { useReportList } from '../hooks/useReportList';
import { activationReportService } from '../services/activationReportService';
import { ReportFilters, TargetRow, TargetType } from '../types';
import { exportFileName, exportList, targetWorkbook } from '../utils/exportReport';
import ExportButton from './ExportButton';
import Pagination from './Pagination';
import ReportState from './ReportState';

interface ByTargetTabProps {
  filters: ReportFilters;
  onChange: (patch: Partial<ReportFilters>) => void;
}

const TYPE_OPTIONS: { value: TargetType; label: string }[] = [
  { value: 'all', label: 'Tất cả' },
  { value: 'book', label: 'Sách' },
  { value: 'collection', label: 'Bộ sách' },
];

const TOP_N = 10;
const LABEL_MAX = 24;

const rowKey = (r: TargetRow) => `${r.target_type}:${r.book_id ?? r.collection_id}`;
const rowTitle = (r: TargetRow) => (r.target_type === 'collection' ? r.collection_title : r.book_title) ?? '';
const truncate = (s: string) => (s.length > LABEL_MAX ? `${s.slice(0, LABEL_MAX - 1)}…` : s);

// Nhãn trục bị cắt ngắn nhưng giữ tên đầy đủ trong <title> để rê chuột xem
const TruncatedTick: React.FC<{ x?: number; y?: number; payload?: { value: string } }> = ({ x, y, payload }) => (
  <text x={x} y={y} dy={4} textAnchor="end" fontSize={12} fill="#4B5563">
    <title>{payload?.value}</title>
    {truncate(payload?.value ?? '')}
  </text>
);

const ByTargetTab: React.FC<ByTargetTabProps> = ({ filters, onChange }) => {
  const setPage = (p: number) => onChange({ page: p });
  const { data, page, perPage, stateProps } = useReportList(useByTarget(filters), filters, setPage);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const type = filters.type ?? 'all';
  const summaryOptions = useSummaryOptions(filters.school_id);

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Biểu đồ chỉ lấy đầu trang 1 (đã sắp theo lượt kích hoạt giảm dần); trang sau không còn là "top"
  const chartData =
    data && page === 1 ? data.items.slice(0, TOP_N).map((r) => ({ name: rowTitle(r), activations: r.activations })) : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
          {TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange({ type: opt.value })}
              aria-pressed={type === opt.value}
              className={`px-3 py-1.5 text-sm font-medium ${
                type === opt.value ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <ExportButton
          onExport={() =>
            exportList(activationReportService.byTarget, filters, (items) => targetWorkbook(items, filters, summaryOptions))
          }
          fileName={exportFileName('theo-sach', filters)}
        />
      </div>

      <ReportState {...stateProps}>
        {data && (
          <div className="space-y-6">
            {chartData.length > 0 && (
              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 10 theo lượt kích hoạt</h3>
                <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 36 + 40)}>
                  <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={170} interval={0} tick={<TruncatedTick />} />
                    <Tooltip />
                    <Bar dataKey="activations" fill="#3B82F6" name="Lượt kích hoạt" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tên</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Lượt kích hoạt</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Người dùng</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Đã thu hồi</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.items.map((row, index) => {
                      const key = rowKey(row);
                      const isOpen = expanded.has(key);
                      const books = row.target_type === 'collection' ? (row.books ?? []) : [];
                      return (
                        <React.Fragment key={key}>
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-500">{(page - 1) * perPage + index + 1}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              <TargetTitle item={row} />
                              {row.target_type === 'collection' && (
                                <button
                                  type="button"
                                  onClick={() => toggle(key)}
                                  aria-expanded={isOpen}
                                  className="ml-2 text-xs text-blue-600 hover:text-blue-800"
                                >
                                  {isOpen ? `Ẩn ${books.length} sách` : `Xem ${books.length} sách`}
                                </button>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                              {row.activations.toLocaleString('vi-VN')}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                              {row.users.toLocaleString('vi-VN')}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                              {row.revoked.toLocaleString('vi-VN')}
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className="bg-gray-50">
                              <td />
                              <td colSpan={4} className="px-4 py-3">
                                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                                  {books.map((b) => (
                                    <li key={b.book_id}>{b.book_title}</li>
                                  ))}
                                </ul>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-xs text-gray-500">
              Chỉ tính mã bị thu hồi; thu hồi tài sản của người dùng không làm giảm số này
            </p>

            <Pagination page={page} perPage={perPage} total={data.total} onPageChange={setPage} />
          </div>
        )}
      </ReportState>
    </div>
  );
};

export default ByTargetTab;
